import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL || '';

// Options arrays (matching the modal)
const coverTypes = [
  'Comprehensive',
  'Third Party Fire and Theft',
  'Third Party Property Damage',
  'Compulsory Third Party'
];

const noClaimBonusOptions = [
  'None', '12 Months (1 Year)', '24 Months (2 Year)', '36 Months (3 Year)',
  '48 Months (4 Year)', '60 Months (5 Year)', '72 Months (6 Year)',
  '84 Months (7 Year)', '96 Months (8 Year)', '108 Months (9 Year)', '120 Months (10 Year)'
];

const itemTypes = [
  'Jewelry', 'Watch', 'Camera', 'Laptop', 'Tablet', 'Mobile Device',
  'Tracking Device', 'Watch-Smart Mobile Device', 'Unspecified', 'Other'
];

// Currency formatting helpers
const formatCurrencyInput = (value: string): string => {
  if (!value) return '';
  const numericValue = value.replace(/[^\d.]/g, '');
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  if (decimalPart.length > 2) decimalPart = decimalPart.substring(0, 2);
  if (wholePart) wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
};

const AddRiskItemPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPolicy = searchParams.get('fromPolicy') === 'true';
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  const policyId = new URLSearchParams(location.search).get('policyId');
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddRiskItemPage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode,
    policyId,
    fromPolicy
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [localSaveMessage, setLocalSaveMessage] = useState('');
  
  // Form state
  const [itemType, setItemType] = useState('');
  const [itemName, setItemName] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [description, setDescription] = useState('');
  const [coverType, setCoverType] = useState('');
  const [insuredValue, setInsuredValue] = useState('');
  const [premium, setPremium] = useState('');
  const [excess, setExcess] = useState('');
  const [noClaimBonus, setNoClaimBonus] = useState('');
  
  const [currencyDisplay, setCurrencyDisplay] = useState({
    insuredValue: '',
    premium: '',
    excess: '',
  });
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch existing risk item if editing
useEffect(() => {
  const editId = new URLSearchParams(location.search).get('editId');
  if (editId && quoteId) {
    setEditingId(editId);
    fetchRiskItemForEdit(editId);
  }
}, [quoteId, location.search, policyId, fromPolicy]); // ✅ Add policyId and fromPolicy
  
  const fetchRiskItemForEdit = async (id: string) => {
  try {
    // ✅ Check localStorage first if it's a temp ID
    if (id.startsWith('temp-')) {
      let storageKey: string;
      
      if (policyId && fromPolicy) {
        storageKey = `policy_${policyId}_risk_item`;
      } else {
        storageKey = `client_${clientId}_risk_items_draft`;
      }
      
      const storedItems = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const foundItem = storedItems.find((item: any) => item.id === id);
      
      if (foundItem) {
        console.log('📦 Found risk item in localStorage:', foundItem);
        // Populate form with found item
        setItemType(foundItem.item_type || '');
        setItemName(foundItem.item_name || '');
        setSerialNumber(foundItem.serial_number || '');
        setDescription(foundItem.description || '');
        setCoverType(foundItem.cover_type || '');
        setInsuredValue((foundItem.insured_value || 0).toString());
        setPremium((foundItem.premium || 0).toString());
        setExcess((foundItem.excess || 0).toString());
        const mapBackendToFrontendNoClaimsBonus = (backendValue: string): string => {
          const mapping: { [key: string]: string } = {
            "None": "None",
            "1 Year": "12 Months (1 Year)",
            "2 Year": "24 Months (2 Year)",
            "3 Year": "36 Months (3 Year)",
            "4 Year": "48 Months (4 Year)",
            "5 Year Plus": "60 Months (5 Year)"
          };
          return mapping[backendValue] || "None";
        };
        
        const rawNoClaimsBonus = foundItem.no_claims_bonus || foundItem.noClaimsBonus || '';
        setNoClaimBonus(mapBackendToFrontendNoClaimsBonus(rawNoClaimsBonus));
        
        const stripZeros = (val: any) => {
          const str = (val || 0).toString();
          return str.endsWith('.00') ? str.slice(0, -3) : str;
        };

        setCurrencyDisplay({
          insuredValue: formatCurrencyInput(stripZeros(foundItem.insured_value)),
          premium: formatCurrencyInput(stripZeros(foundItem.premium)),
          excess: formatCurrencyInput(stripZeros(foundItem.excess)),
        });
        return;
      }
    }
    
    // Not found in localStorage or not a temp ID - fetch from API
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/risk-items/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const riskItem = response.data.data || response.data;
    
    console.log('📦 Fetched risk item for edit:', riskItem);
    
    // ✅ Map the backend format ("3 Year") to the frontend format ("36 Months (3 Year)")
    const mapBackendToFrontendNoClaimsBonus = (backendValue: string): string => {
      const mapping: { [key: string]: string } = {
        "None": "None",
        "1 Year": "12 Months (1 Year)",
        "2 Year": "24 Months (2 Year)",
        "3 Year": "36 Months (3 Year)",
        "4 Year": "48 Months (4 Year)",
        "5 Year Plus": "60 Months (5 Year)",
        "12 Months (1 Year)": "12 Months (1 Year)",
        "24 Months (2 Year)": "24 Months (2 Year)",
        "36 Months (3 Year)": "36 Months (3 Year)",
        "48 Months (4 Year)": "48 Months (4 Year)",
        "60 Months (5 Year)": "60 Months (5 Year)",
        "72 Months (6 Year)": "72 Months (6 Year)",
        "84 Months (7 Year)": "84 Months (7 Year)",
        "96 Months (8 Year)": "96 Months (8 Year)",
        "108 Months (9 Year)": "108 Months (9 Year)",
        "120 Months (10 Year)": "120 Months (10 Year)"
      };
      return mapping[backendValue] || "None";
    };

    const rawNoClaimsBonus = riskItem.no_claims_bonus || riskItem.noClaimsBonus || '';
    const frontendNoClaimsBonus = mapBackendToFrontendNoClaimsBonus(rawNoClaimsBonus);
    
    const mapBackendItemTypeToFrontend = (backendType: string): string => {
      const mapping: { [key: string]: string } = {
        'Jewellery': 'Jewelry',
        'Watch-Smart Mobile Device': 'Watch',
        'Camera / Equipment': 'Camera',
        'Laptops': 'Laptop',
        'Tablet-Mobile Device': 'Tablet',
        'Mobile Phone': 'Mobile Device',
        'Tracking Device': 'Tracking Device',
        'Unspecified': 'Unspecified',
        'Other': 'Other'
      };
      return mapping[backendType] || backendType;
    };

    setItemType(mapBackendItemTypeToFrontend(riskItem.item_type || riskItem.itemType || ''));
    setItemName(riskItem.item_name || riskItem.itemName || '');
    setSerialNumber(riskItem.serial_number || riskItem.serialNumber || '');
    setDescription(riskItem.description || '');
    setCoverType(riskItem.cover_type || riskItem.coverType || '');
    setInsuredValue((riskItem.insured_value || riskItem.insuredValue || 0).toString());
    setPremium((riskItem.premium || 0).toString());
    setExcess((riskItem.excess || 0).toString());
    setNoClaimBonus(frontendNoClaimsBonus);
    
    // Strip trailing .00 for cleaner display
    const stripZeros = (val: any) => {
      const str = (val || 0).toString();
      return str.endsWith('.00') ? str.slice(0, -3) : str;
    };

    setCurrencyDisplay({
      insuredValue: formatCurrencyInput(stripZeros(riskItem.insured_value || riskItem.insuredValue)),
      premium: formatCurrencyInput(stripZeros(riskItem.premium)),
      excess: formatCurrencyInput(stripZeros(riskItem.excess)),
    });
    
  } catch (error) {
    console.error('Error fetching risk item for edit:', error);
  }
};
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    switch (name) {
      case 'itemType': setItemType(value); break;
      case 'itemName': setItemName(value); break;
      case 'serialNumber': setSerialNumber(value); break;
      case 'description': setDescription(value); break;
      case 'coverType': setCoverType(value); break;
      case 'insuredValue':
        setCurrencyDisplay(prev => ({ ...prev, insuredValue: formatCurrencyInput(value) }));
        setInsuredValue(parseCurrencyInput(value).toString());
        break;
      case 'premium':
        setCurrencyDisplay(prev => ({ ...prev, premium: formatCurrencyInput(value) }));
        setPremium(parseCurrencyInput(value).toString());
        break;
      case 'excess':
        setCurrencyDisplay(prev => ({ ...prev, excess: formatCurrencyInput(value) }));
        setExcess(parseCurrencyInput(value).toString());
        break;
      case 'noClaimBonus': setNoClaimBonus(value); break;
      default: break;
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!itemType) newErrors.itemType = 'Item Type is required';
    if (!itemName) newErrors.itemName = 'Item Name is required';
    if (!coverType) newErrors.coverType = 'Cover Type is required';
    if (!insuredValue || parseFloat(insuredValue) <= 0) newErrors.insuredValue = 'Insured Value is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
    const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isSaving) return;
  if (!validateForm()) return;
  
  // Validate clientId
  if (!clientId || isNaN(parseInt(clientId))) {
    setErrors(prev => ({ ...prev, server: 'Valid client ID is required' }));
    return;
  }
  
  setIsSaving(true);
  setLocalSaveMessage('Saving risk item...');
  
  // Clean the quoteId - use undefined instead of null
  let cleanQuoteId: number | undefined = undefined;
  if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
    cleanQuoteId = parseInt(quoteId);
  }
  
  // Helper function to convert to number
  const toNumber = (value: any): number => {
    if (value === undefined || value === null || value === '') return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };
  
  // Just use the exact string the user selected
  const backendNoClaimsBonus = noClaimBonus;

  // Map frontend item types to backend expected values
  const mapItemTypeForBackend = (frontendType: string): string => {
    const mapping: { [key: string]: string } = {
      'Jewelry': 'Jewellery',
      'Watch': 'Watch-Smart Mobile Device',
      'Camera': 'Camera / Equipment',
      'Laptop': 'Laptops',
      'Tablet': 'Tablet-Mobile Device',
      'Mobile Device': 'Mobile Phone',
      'Tracking Device': 'Tracking Device',
      'Watch-Smart Mobile Device': 'Watch-Smart Mobile Device',
      'Unspecified': 'Unspecified',
      'Other': 'Other'
    };
    return mapping[frontendType] || frontendType;
  };

  // Map coverType to backend expected values
  const mapCoverTypeForBackend = (frontendType: string): string => {
    const mapping: { [key: string]: string } = {
      'Comprehensive': 'Comprehensive',
      'Theft Cover Excluding': 'Third Party Fire and Theft',
      'Flood Cover Excluding': 'Third Party Property Damage',
      'Advensure': 'Compulsory Third Party'
    };
    return mapping[frontendType] || frontendType;
  };
  
  // ✅ Send original values without mapping
  const submitData = {
    item_type: mapItemTypeForBackend(itemType),  // ✅ Map to backend expected values
    item_name: itemName,
    serial_number: serialNumber,
    description: description,
    cover_type: mapCoverTypeForBackend(coverType),
    insured_value: toNumber(insuredValue),
    premium: toNumber(premium),
    excess: toNumber(excess),
    no_claims_bonus: backendNoClaimsBonus,
    client_id: parseInt(clientId),
    quote_id: cleanQuoteId
  };

  console.log('📤 Submitting risk item data (mapped for backend):', submitData);
  
  try {
    const token = localStorage.getItem('token');
    
    // If no quote_id, save to localStorage (draft mode)
    if (!cleanQuoteId) {
      console.log('💾 No quote ID - saving risk item to localStorage');
      
      // ✅ Determine the storage key based on context
      let storageKey: string;
      
      // ✅ EXTRA SAFETY: Only use policy key if we're explicitly coming from policy
      if (policyId && fromPolicy === true) {
        // Coming from Policy Detail page - save with policy ID key
        storageKey = `policy_${policyId}_risk_item`;
        console.log(`📂 Saving risk item with policy key: ${storageKey}`);
      } else {
        // Coming from Quote/Create Quote page - save with client draft key
        storageKey = `client_${clientId}_risk_items_draft`;
        console.log(`📂 Saving risk item with client draft key: ${storageKey}`);
      }
      
      const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Check if we're editing an existing draft
      let updatedDrafts;
      if (editingId && editingId.startsWith('temp-')) {
        // Update existing draft
        updatedDrafts = existingDrafts.map((item: any) => 
          item.id === editingId 
            ? { 
                ...item, 
                ...submitData, 
                id: editingId,
                isTemp: true,
                status: 'active',
                inception_date: new Date().toISOString().split('T')[0],
                product_type: 'risk-items'
              }
            : item
        );
      } else {
        // Add new draft
        updatedDrafts = [
          ...existingDrafts,
          {
            ...submitData,
            id: `temp-${Date.now()}`,
            isTemp: true,
            status: 'active',
            inception_date: new Date().toISOString().split('T')[0],
            product_type: 'risk-items'
          }
        ];
      }
      
      localStorage.setItem(storageKey, JSON.stringify(updatedDrafts));
      
      setLocalSaveMessage('Risk item saved to draft!');
      
      // ✅ Navigate based on context
      setTimeout(() => {
        if (policyId && fromPolicy) {
          // Return to policy detail page
          const returnUrl = returnTo || `/policy/${policyId}`;
          const separator = returnUrl.includes('?') ? '&' : '?';
          const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=risk_item&clientId=${clientId}`;
          console.log('📍 Returning to policy detail page:', finalUrl);
          navigate(finalUrl);
        } else if (isMasterQuoteMode && returnTo) {
          // Master quote mode - navigate back to master quote page
          const decodedReturnUrl = decodeURIComponent(returnTo);
          const separator = decodedReturnUrl.includes('?') ? '&' : '?';
          const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=risk_item&clientId=${clientId}`;
          console.log('📍 Returning to master quote page:', finalUrl);
          navigate(finalUrl);
        } else {
          // Create quote mode
          navigate(`/create-quote/${clientId}`, { 
            state: { refreshProducts: true, hasDrafts: true }
          });
        }
      }, 1500);
    } else {
      // Has quote_id - save to database
      if (editingId) {
        await axios.put(`${API_URL}/api/risk-items/${editingId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLocalSaveMessage('Risk item updated successfully!');
        
        // ✅ LOG ACTIVITY: Risk Item Updated
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const performedBy = user?.username || user?.email || 'System';
          
          await axios.post(
            `${API_URL}/api/quote-activity-logs`,
            {
              quote_id: cleanQuoteId,
              action_type: 'product_updated',
              description: `Risk item updated: ${itemName || ''}`.trim() || 'Risk Item',
              metadata: {
                productType: 'risk_item',
                productId: editingId,
                performedBy: performedBy,
                itemName: itemName || '',
                itemType: itemType || '',
                serialNumber: serialNumber || '',
                insuredValue: parseFloat(insuredValue) || 0,
                premium: parseFloat(premium) || 0,
                excess: parseFloat(excess) || 0,
                coverType: coverType || '',
                updatedAt: new Date().toISOString()
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Activity logged: risk_item_updated');
        } catch (logError) {
          console.error('❌ Failed to log risk item update:', logError);
          // Don't fail the update if logging fails
        }
      } else {
        await axios.post(`${API_URL}/api/risk-items`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLocalSaveMessage('Risk item added successfully!');
        
        // ✅ LOG ACTIVITY: Risk Item Added
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const performedBy = user?.username || user?.email || 'System';
          
          await axios.post(
            `${API_URL}/api/quote-activity-logs`,
            {
              quote_id: cleanQuoteId,
              action_type: 'product_added',
              description: `Risk item added: ${itemName || ''}`.trim() || 'Risk Item',
              metadata: {
                productType: 'risk_item',
                performedBy: performedBy,
                itemName: itemName || '',
                itemType: itemType || '',
                serialNumber: serialNumber || '',
                insuredValue: parseFloat(insuredValue) || 0,
                premium: parseFloat(premium) || 0,
                excess: parseFloat(excess) || 0,
                coverType: coverType || '',
                addedAt: new Date().toISOString()
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Activity logged: risk_item_added');
        } catch (logError) {
          console.error('❌ Failed to log risk item addition:', logError);
          // Don't fail the addition if logging fails
        }
      }
      
      // ✅ FIX: Navigate based on mode
      setTimeout(() => {
        const validClientId = clientId || new URLSearchParams(location.search).get('clientId');
        
        if (!validClientId) {
          console.error('❌ No clientId available for navigation');
          setLocalSaveMessage('Error: Missing client ID');
          setIsSaving(false);
          return;
        }
        
        if (isMasterQuoteMode && returnTo) {
          // Master quote mode - navigate back to master quote page
          const decodedReturnUrl = decodeURIComponent(returnTo);
          const separator = decodedReturnUrl.includes('?') ? '&' : '?';
          const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=risk_item&clientId=${validClientId}`;
          console.log('📍 Returning to master quote page:', finalUrl);
          navigate(finalUrl);
        } else if (cleanQuoteId) {
          // Create quote mode but with quoteId - navigate to create quote page
          navigate(`/create-quote/${validClientId}`, { 
            state: { refreshProducts: true, activeTab: 'quotes' } 
          });
        } else {
          // Fallback
          navigate(`/create-quote/${validClientId}`, { 
            state: { refreshProducts: true } 
          });
        }
      }, 1500);
    }
      
  } catch (error: any) {
    console.error('Error saving risk item:', error);
    console.error('Backend error response:', error.response?.data);
    console.error('Backend error status:', error.response?.status);
    console.error('Full backend error:', JSON.stringify(error.response?.data, null, 2));
    setLocalSaveMessage(error.response?.data?.message || error.response?.data?.error || 'Failed to save risk item');
    setIsSaving(false);
  }
};
  
  if (!user) return <div>Loading...</div>;
  
  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Risk Item' : 'Add Risk Item'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">

            <button
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=risk_item&clientId=${clientId}`;
      console.log('📍 Back button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=risk_item&clientId=${clientId}`;
      console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      // Go back to create quote page
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
>
  ← {fromPolicy ? 'Back to Policy Details' : 'Back to Quote'}
</button>
            
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6">
                {editingId ? 'Edit Risk Item Details' : 'Add Risk Item Details'}
              </h2>
              
              {localSaveMessage && (
                <div className={`mb-4 p-3 rounded ${localSaveMessage.includes('❌') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {localSaveMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Item Type */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Item Type</label>
                    <select
                      name="itemType"
                      value={itemType}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.itemType ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="" disabled>Select Item Type</option>
                      {itemTypes.map(type => <option key={type} value={type}>{type}</option>)}
                      <option value="Other">Other</option>
                    </select>
                    {errors.itemType && <p className="text-red-500 text-xs mt-1">{errors.itemType}</p>}
                  </div>
                  
                  {/* Item Name */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Item Name</label>
                    <input
                      type="text"
                      name="itemName"
                      value={itemName}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.itemName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="e.g., Rolex Submariner"
                    />
                    {errors.itemName && <p className="text-red-500 text-xs mt-1">{errors.itemName}</p>}
                  </div>
                  
                  {/* Serial Number */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Serial Number</label>
                    <input
                      type="text"
                      name="serialNumber"
                      value={serialNumber}
                      onChange={handleInputChange}
                      className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="e.g., SN12345678"
                    />
                  </div>
                  
                  {/* Cover Type */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Cover Type</label>
                    <select
                      name="coverType"
                      value={coverType}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.coverType ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="" disabled>Select Cover Type</option>
                      {coverTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                    {errors.coverType && <p className="text-red-500 text-xs mt-1">{errors.coverType}</p>}
                  </div>
                </div>
                
                {/* Description */}
                <div className="mb-6">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Description</label>
                  <textarea
                    name="description"
                    value={description}
                    onChange={handleInputChange}
                    rows={4}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="Describe the item..."
                  />
                </div>
                
                {/* Insured Value and No Claim Bonus */}
                <div className="flex space-x-6 mb-6">
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        name="insuredValue"
                        value={currencyDisplay.insuredValue}
                        onChange={handleInputChange}
                        className={`shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.insuredValue ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="0.00"
                      />
                    </div>
                    {errors.insuredValue && <p className="text-red-500 text-xs mt-1">{errors.insuredValue}</p>}
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">No Claim Bonus</label>
                    <select
                      name="noClaimBonus"
                      value={noClaimBonus}
                      onChange={handleInputChange}
                      className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    >
                      <option value="" disabled>Select bonus</option>
                      
                      {/* Fallback option for unrecognized backend values */}
                      {noClaimBonus && !noClaimBonusOptions.includes(noClaimBonus) && (
                        <option value={noClaimBonus}>{noClaimBonus}</option>
                      )}

                      {noClaimBonusOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Premium and Excess */}
                <div className="flex space-x-6 mb-6">
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Premium</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        name="premium"
                        value={currencyDisplay.premium}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        name="excess"
                        value={currencyDisplay.excess}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">

                  <button
  type="button"
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=risk_item&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=risk_item&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg"
>
  Cancel
</button>

                  <button
                    type="submit"
                    disabled={isSaving}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-blue-300"
                  >
                    {isSaving ? 'Saving...' : (editingId ? 'Update Risk Item' : 'Add Risk Item')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRiskItemPage;